import React, { createContext, useContext, useEffect, useState, useRef, useCallback, ReactNode } from 'react';
import * as signalR from '@microsoft/signalr';
import AuthService from '../core/JWThelper'

interface SignalRContextType {
    connectionState: string;
    sendMessage: (methodName: string, ...args: any[]) => Promise<void>;
    addEventListener: (eventName: string, handler: (...args: any[]) => void) => void;
    removeEventListener: (eventName: string, handler: (...args: any[]) => void) => void;
}

const SignalRContext = createContext<SignalRContextType | undefined>(undefined);

export const useSignalR = (): SignalRContextType => {
    const context = useContext(SignalRContext);
    if (!context) {
        throw new Error('useSignalR must be used within a SignalRProvider');
    }
    return context;
};

interface SignalRProviderProps {
    hubUrl: string;
    children: ReactNode;
}

export const SignalRProvider: React.FC<SignalRProviderProps> = ({ hubUrl, children }) => {
    const [connectionState, setConnectionState] = useState<string>('disconnected');
    const connectionRef = useRef<signalR.HubConnection | null>(null);
    const eventHandlersRef = useRef<{ [key: string]: Array<(...args: any[]) => void> }>({});
    const personId = AuthService.getCurrentUserPersonId();

    useEffect(() => {
        // the hub requires the token and takes the person from it
        if (!personId) {
            return;
        }
        const connection = new signalR.HubConnectionBuilder()
            .withUrl(hubUrl, {
                // read on every (re)connect, so a renewed token is picked up
                accessTokenFactory: () => localStorage.getItem('accessToken') ?? ''
            })
            // .withAutomaticReconnect()
            .build();

        connectionRef.current = connection;

        const startConnection = async () => {
            try {
                await connection.start();
                setConnectionState('connected');
                // Re-attach event handlers after connection is established
                for (const [event, handlers] of Object.entries(eventHandlersRef.current)) {
                    handlers.forEach(handler => connection.on(event, handler));
                }
            } catch (err) {
                console.error('Error while establishing connection: ', err);
                setConnectionState('disconnected');
                // setTimeout(startConnection, 5000); // Retry connection after 5 seconds
            }
        };

        let stopped = false;
        connection.onclose(() => {
            setConnectionState('disconnected');
            if (!stopped) {
                startConnection(); // Restart connection on close
            }
        });

        startConnection();

        return () => {
            // the person changed or the provider unmounted: don't let onclose reconnect this connection
            stopped = true;
            connection.stop();
        };
    }, [hubUrl, personId]);

    const sendMessage = useCallback(async (methodName: string, ...args: any[]) => {
        if (connectionRef.current && connectionState === 'connected') {
            try {
                await connectionRef.current.invoke(methodName, ...args);
            } catch (err) {
                console.error('Error while sending message: ', err);
            }
        }
    }, [connectionState]);

    const addEventListener = useCallback((eventName: string, handler: (...args: any[]) => void) => {
        if (!eventHandlersRef.current[eventName]) {
            eventHandlersRef.current[eventName] = [];
        }
        eventHandlersRef.current[eventName].push(handler);
        if (connectionState === 'connected') {
            connectionRef.current?.on(eventName, handler);
        }
    }, [connectionState]);

    const removeEventListener = useCallback((eventName: string, handler: (...args: any[]) => void) => {
        if (eventHandlersRef.current[eventName]) {
            eventHandlersRef.current[eventName] = eventHandlersRef.current[eventName].filter(h => h !== handler);
            if (connectionState === 'connected') {
                connectionRef.current?.off(eventName, handler);
            }
        }
    }, [connectionState]);

    return (
        <SignalRContext.Provider value={{ connectionState, sendMessage, addEventListener, removeEventListener }}>
            {children}
        </SignalRContext.Provider>
    );
};
